// src/components/PrintTicket.jsx
//
// The shop's working order ticket, laid out to match the eFlorist form the
// staff already read fluently: sans-serif (not Courier), everything in ruled
// boxes, and the numbers in a horizontal strip rather than a right-aligned
// stack. The previous monospace version held the same information but was
// noticeably harder to scan.
//
// ONE landscape page that fills the sheet. It previously also printed a card
// slip and a handover label on a second page, which just repeated the order
// number, payment band, recipient and items — so those were dropped and the
// card message moved into the Order Notes column instead.

import { createPortal } from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { schedule, workflowOf, neededDateOf, neededTimeOf, fmtDay, fmtTime } from '../lib/priority';
import { paymentSummary, cancellationOf } from '../lib/invoice';

const fmt = (n) => '$' + (Number(n) || 0).toFixed(2);

const TYPE_TITLE = {
  carry_out: 'Carry Out', local_delivery: 'Local Delivery',
  pick_up: 'Pick Up', wire_out: 'Wire Out', wire_in: 'Wire In',
};

// en-US throughout, to match the M/D/YYYY and 12-hour clock the shop's existing
// forms use. en-CA would render these as 2026-07-31 and 16:30, which is what
// the first draft of this ticket did.

/** "7/31/2026 4:30 PM - Friday", with the time when one was given. */
function fmtNeededDate(dateStr, timeStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');   // noon: timezone can't flip the day
  const date = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const day  = d.toLocaleDateString('en-US', { weekday: 'long' });
  return `${date}${timeStr ? ` ${fmtTime(timeStr)}` : ''} - ${day}`;
}

function fmtTaken(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const STATUS_WORD = {
  pending: 'Pending', draft: 'Draft', ready: 'Ready',
  completed: 'Completed', paid: 'Paid', void: 'CANCELLED',
};
const DESIGN_WORD = {
  not_started: 'Not made', in_progress: 'In progress', done: 'Made',
};

export function PrintTicket({ order }) {
  const { shop } = useAuth();
  if (!order) return null;

  const neededDate = neededDateOf(order) || '';
  const neededTime = neededTimeOf(order);
  const customerName = order.customerName
    || `${order.billing?.firstName || ''} ${order.billing?.lastName || ''}`.trim();
  const recipientName = `${order.recipient?.firstName || ''} ${order.recipient?.lastName || ''}`.trim();

  const sched = schedule(order, workflowOf(shop));
  const pay   = paymentSummary(order);
  const cancel = cancellationOf(order);
  const title = TYPE_TITLE[order.type] || 'Order';
  const dateLabel = order.type === 'pick_up' ? 'Pick Up Date'
                  : order.type === 'carry_out' ? 'Needed Date'
                  : 'Delivery Date';

  const b = order.billing || {};
  const r = order.recipient || {};
  const otherFees = Number(order.serviceFee) || 0;

  // Rendered into <body> rather than inside the app tree. During print the
  // app is hidden with `display:none`, which lets this document start at the
  // top of the page and flow onto further pages — with the old approach the
  // app still occupied space, forcing position:fixed and clipping anything
  // past a single page.
  return createPortal(
    <div className="tk-print-root">
    <div id="print-ticket">

      {/* ── SECTION 1: the order form ───────────────────────────────── */}
      <div className="tk-page">
        <div className="tk-title">{title}</div>

        {cancel.cancelled && (
          <>
            {/* "Cancelled" is what the shop says; the stored status stays
                'void' so older orders still read correctly. */}
            <div className="tk-void">
              *** CANCELLED — DO NOT MAKE THIS ORDER ***
              {cancel.reason ? ` (${cancel.reason})` : ''}
            </div>
            {/* Cancelling doesn't return the money. Without this the ticket
                said CANCELLED and PAID, and nothing about the refund. */}
            {cancel.refundDue && (
              <div className="tk-void">REFUND DUE — {fmt(cancel.refundAmount)} BACK TO CUSTOMER</div>
            )}
            {cancel.refunded && (
              <div className="tk-row"><b>Refunded:</b> {fmt(cancel.refundAmount)}</div>
            )}
          </>
        )}

        {/* Header: order identity on the left, shop identity on the right */}
        <div className="tk-head">
          <div className="tk-head-col">
            <Field label="Order #"      value={order.orderNumber} strong/>
            <Field label="Order Status" value={STATUS_WORD[order.status] || order.status || '—'}/>
            <Field label="Order Date"   value={fmtTaken(order.createdAt)}/>
            <Field label={dateLabel}    value={fmtNeededDate(neededDate, neededTime)} strong/>
          </div>
          <div className="tk-head-col">
            <div className="tk-shop">{shop?.name}</div>
            <Field label="Sold By"  value={order.receivedBy || '—'}/>
            <Field
              label="Design"
              value={`${DESIGN_WORD[sched.design] || '—'}${
                sched.design === 'done' && order.fulfillment?.designedBy
                  ? ` (${order.fulfillment.designedBy})` : ''}`}
            />
            {/* Already gone out. Printed so a reprint can't lead to the same
                order being handed over a second time. */}
            {sched.handedOff && (
              <Field
                label="Handover"
                value={order.type === 'local_delivery' ? 'DELIVERED' : 'PICKED UP'}
                strong
              />
            )}
            {sched.finishBy && <Field label="MAKE BY" value={fmtDay(sched.finishBy, { withYear: true })} strong/>}
          </div>
        </div>

        {/* Payment status band — the thing you must not get wrong on handover.
            When unpaid it names no payment method: what was selected at order
            time isn't necessarily what the customer hands over at the counter,
            and printing it invites taking the wrong kind of payment. */}
        {!cancel.cancelled && (
          <div className={`tk-paidband ${pay.settled ? 'is-paid' : 'is-due'}`}>
            {pay.settled
              ? <>PAID{pay.method ? ` — ${pay.method}` : ''}</>
              : <>NOT PAID — COLLECT {fmt(pay.amountDue || order.total)}</>}
          </div>
        )}

        {sched.outOfTown && (
          <div className="tk-flightband">
            ✈ OUT OF TOWN — {sched.flight?.airline || ''} {sched.flight?.number || ''}
            {sched.flight?.departTime && <> departs {fmtTime(sched.flight.departTime)}</>}
            {sched.flightDate && <> {fmtDay(sched.flightDate, { withYear: true })}</>}
            {sched.dropOffBy && <> — AT AIRPORT BY {fmtTime(sched.dropOffBy)}</>}
          </div>
        )}

        {/* Four-column party block */}
        <div className="tk-grid tk-grid-4">
          <Box title="Customer">
            <div className="tk-strong">{customerName || '—'}</div>
            {b.address && <div>{b.address}</div>}
            {(b.city || b.stPr) && <div>{[b.city, b.stPr].filter(Boolean).join(', ')} {b.zipPostal || ''}</div>}
            {b.phone && <div>{b.phone}</div>}
            {b.email && <div>{b.email}</div>}
            {order.customerId && <div className="tk-sub">Customer ID: {order.customerId}</div>}
          </Box>

          <Box title="Recipient">
            {recipientName ? <div className="tk-strong">{recipientName}</div> : <div>—</div>}
            {r.address && <div>{r.address}</div>}
            {(r.city || r.stPr) && <div>{[r.city, r.stPr].filter(Boolean).join(', ')} {r.zipPostal || ''}</div>}
            {r.country && <div>{r.country}</div>}
            {r.phone && <div>{r.phone}</div>}
            {order.delivery?.zone && <div className="tk-sub">Del. Area: {order.delivery.zone}</div>}
          </Box>

          <Box title="Sent to / from">
            {order.wireOut?.sendVia && <div>{order.wireOut.sendVia}</div>}
            {order.wireOut?.fillingFlorist && <div>Dove Ord# {order.wireOut.fillingFlorist}</div>}
            {order.wireIn?.receivedVia && <div>{order.wireIn.receivedVia}</div>}
            {order.wireIn?.floristId && <div>Florist ID: {order.wireIn.floristId}</div>}
            {order.wireIn?.refOrderNumber && <div>Ref# {order.wireIn.refOrderNumber}</div>}
            {b.auth && <div className="tk-sub">CC Auth: {b.auth}</div>}
          </Box>

          <Box title={`Occasion  ${r.occasion || 'Other'}`}>
            <div className="tk-sub">Card Message</div>
            <div className="tk-cardmsg-sm">{r.cardMessage || ''}</div>
          </Box>
        </div>

        {/* Money strip */}
        <div className="tk-money">
          <span><b>Subtotal</b> {fmt(order.subtotal)}</span>
          <span><b>Delivery Fee</b> {fmt(order.deliveryFee)}</span>
          <span><b>Other Fees</b> {fmt(otherFees)}</span>
          <span><b>Tax</b> {fmt(order.tax)}</span>
        </div>
        <div className="tk-money tk-money-total">
          <span><b>Total</b> {fmt(order.total)}</span>
          {/* Only state a method once money has been taken. An unpaid order
              states what is still owed instead — printing a method beside the
              total reads as though it had already been collected. */}
          <span className="tk-method">
            {pay.settled
              ? <><b>Method(s) of Payment</b> {pay.method || '—'} {fmt(order.total)}</>
              : <><b>Balance Due</b> {fmt(pay.amountDue || order.total)}</>}
          </span>
        </div>

        {/* Items down the left, notes down the right — both stretch to fill the
            sheet, so the description box has room to write in rather than
            leaving the bottom half of the page blank. */}
        <div className="tk-mid">
          <div className="tk-itembox">
            <table className="tk-items">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="tk-c">QTY</th>
                  <th className="tk-r">Price</th>
                  <th className="tk-r">Disc.</th>
                  <th className="tk-r">Ext. Price</th>
                </tr>
              </thead>
              <tbody>
                {(order.items || []).filter(i => i.description).map((item, i) => {
                  const qty = Number(item.qty) || 0;
                  const price = Number(item.price) || 0;
                  const disc = Number(item.discount) || 0;
                  return (
                    <tr key={i}>
                      <td>{item.description}</td>
                      <td className="tk-c">{qty}</td>
                      <td className="tk-r">{fmt(price)}</td>
                      <td className="tk-r">{disc ? disc + '%' : fmt(0)}</td>
                      <td className="tk-r">{fmt(qty * price * (1 - disc / 100))}</td>
                    </tr>
                  );
                })}
                {!(order.items || []).some(i => i.description) && (
                  <tr><td colSpan={5} className="tk-sub">No items on this order.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="tk-notesbox">
            <div className="tk-box-title">Order Notes</div>
            <div className="tk-box-body">
              {sched.reasons.length > 0 && <div>Priority: {sched.reasons.map(x => x.label).join(' · ')}</div>}
              {sched.startBy && sched.startBy !== sched.finishBy && (
                <div>Start by {fmtDay(sched.startBy, { withYear: true })}</div>
              )}
              {order.delivery?.driver && <div>Driver: {order.delivery.driver}</div>}
              {order.delivery?.designer && order.delivery.designer !== 'None' && (
                <div>Designer: {order.delivery.designer}</div>
              )}
              {r.cardMessage && (
                <div className="tk-notes-card">
                  <div className="tk-sub">Card Message</div>
                  <div className="tk-cardmsg-sm">{r.cardMessage}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer: instructions + signature */}
        <div className="tk-grid tk-grid-2 tk-foot">
          <Box title="Special Instructions">
            {order.delivery?.instructions || ''}
          </Box>
          <Box title="Delivery / Pick Up Signature">
            <div className="tk-siglines">
              <div className="tk-sigline"/>
              <div className="tk-sub">Received by</div>
            </div>
          </Box>
        </div>
      </div>

    </div>
    </div>,
    document.body
  );
}

function Field({ label, value, strong }) {
  return (
    <div className="tk-field">
      <span className="tk-field-label">{label}:</span>
      <span className={strong ? 'tk-strong' : ''}>{value}</span>
    </div>
  );
}

function Box({ title, children }) {
  return (
    <div className="tk-box">
      <div className="tk-box-title">{title}</div>
      <div className="tk-box-body">{children}</div>
    </div>
  );
}
